import React, { useState } from 'react';
import { TransactionError } from '@thirdweb-dev/react';
import { Box, Button, Divider, HStack, Text } from '@chakra-ui/react';
import { NetworkMismatchButton } from '@app/components/NetworkMismatchButton';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { TransactionResultModal } from '@app/components/TransactionResultModal';
import {
  useAllocateTokens,
  useXKimTokenApprove
} from '@app/hooks/ryo/useAllocate';
import { useDividentsStats } from '@app/hooks/ryo/useDividentsStats';
import { Icon } from '@app/components/Icon';
import { LabeledValue } from '@app/components/LabeledValue';
import { toOptionalFixed } from '@app/helpers/format';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';
import { useDeallocate } from '@app/hooks/ryo/useDeallocate';

export const Allocate = () => {
  const [feedback, setFeedback] = useState<{
    status: 'success' | 'error';
    title: string;
    message: string;
    txHash?: string;
  } | null>(null);
  const {
    state,
    handleAmountChange,
    myXRyoBalance,
    allocate,
    allocating,
    loadingTokenBalance,
    resetStateValues
  } = useAllocateTokens();
  const isMismatchedNetwork = useNetworkMismatch();
  const { userAllocation, totalAllocation, dividendsShare } =
    useDividentsStats();
  const { usageAllocation } = useDeallocate();

  const {
    requiresApproval: requiresApproval,
    approve,
    isApproveInProgress: isApproveInProgress,
    getAllowance: getAllowance
  } = useXKimTokenApprove(state.amount);

  const isBalanceExceeded =
    myXRyoBalance !== undefined && +myXRyoBalance < +state.amount;

  function renderInfoRow(label: string, value0: string, value1?: string) {
    return (
      <HStack justifyContent="space-between">
        <Text fontSize="16px" fontWeight={400} lineHeight="26px" color="gray.4">
          {label}
        </Text>
        <HStack>
          <Text
            fontSize="16px"
            fontWeight={500}
            lineHeight="26px"
            color="gray.4"
          >
            {value0}
          </Text>
          {value1 && (
            <>
              <Icon
                name="arrowRight"
                style={{ color: 'var(--chakra-colors-gray-4)' }}
              />
              <Text
                fontSize="16px"
                fontWeight={500}
                lineHeight="26px"
                color="gray.4"
              >
                {value1}
              </Text>
            </>
          )}
        </HStack>
      </HStack>
    );
  }

  function getNextDividendsShare() {
    const nextAllocation = +userAllocation + +state.amount;
    const nextTotalAllocation = +totalAllocation + +state.amount;

    return (nextAllocation * 100) / nextTotalAllocation;
  }

  function getSubmitLabel() {
    if (isBalanceExceeded) {
      return 'Insufficient balance';
    }

    if (!state.amount) {
      return 'Enter xKIM amount to allocate';
    }

    return 'Allocate';
  }

  function renderSubmitAction() {
    if (isMismatchedNetwork) {
      return <NetworkMismatchButton />;
    }

    if (requiresApproval && !isBalanceExceeded) {
      return (
        <SubmitButton
          mt="40px"
          loadingText="Processing..."
          onClick={async () => {
            try {
              const res = await approve();

              await getAllowance();

              const txHash = res?.receipt?.transactionHash;

              showSuccessToast(
                `Successfully approved KIM`,
                <ExternalLink txHash={txHash} />
              );
            } catch (e) {
              if (e instanceof TransactionError) {
                showErrorToast(e.reason);
              } else {
                showErrorToast(`Error approving KIM`);
              }
            }
          }}
          isLoading={isApproveInProgress}
          label="Approve"
        />
      );
    }

    return (
      <SubmitButton
        disabled={isBalanceExceeded || !+state.amount}
        mt="40px"
        onClick={async () => {
          try {
            const res = await allocate();

            if (res['reason'] !== undefined) {
              showErrorToast(res.reason);
            } else if (res instanceof Error) {
              showErrorToast(res.message);
            } else {
              resetStateValues();

              const txHash = res?.receipt?.transactionHash;

              showSuccessToast(
                `Successfully converted KIM to xKIM`,
                <ExternalLink txHash={txHash} />
              );

              setFeedback({
                status: 'success',
                title: 'Transaction Confirmed',
                message: 'Your transaction has been successfully completed',
                txHash
              });
            }
          } catch (e) {
            if (e instanceof TransactionError) {
              showErrorToast(e.reason);
            } else {
              showErrorToast(`Error converting KIM to xKIM`);
            }

            setFeedback({
              status: 'error',
              title: 'Transaction Error',
              message:
                'There was an error processing your transaction. Please check the details and try again'
            });
          }
        }}
        isLoading={allocating}
        label={getSubmitLabel()}
      />
    );
  }

  return (
    <Box mt="52px">
      <HStack justifyContent="space-between">
        <Text fontSize="16px" fontWeight={400} lineHeight="26px">
          Enter xKIM amount
        </Text>

        <LabeledValue
          label="My xKIM:"
          isLoading={loadingTokenBalance}
          value={!myXRyoBalance ? 0 : toOptionalFixed(+myXRyoBalance, 2)}
        />
      </HStack>
      <HStack
        justifyContent="space-between"
        mt="18px"
        sx={{
          paddingRight: '14px',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'gray.5',
          background: 'black'
        }}
      >
        <DebouncedInput
          amount={state.amount}
          onChange={handleAmountChange}
          textAlign="left"
        />
        <Button
          variant="tertiary"
          onClick={() => {
            if (myXRyoBalance === undefined) {
              return;
            }

            handleAmountChange(myXRyoBalance.toString());
          }}
          sx={{
            color: 'orange',
            bg: 'gray.2',
            width: 'fit-content',
            fontSize: '14px',
            padding: '8px 14px'
          }}
        >
          MAX
        </Button>
      </HStack>
      {+state.amount > 0 && (
        <>
          <Divider
            sx={{ margin: '28px 0', background: 'gray.5', height: '1px' }}
          />
          {renderInfoRow(
            'Allocation',
            `${
              usageAllocation ? toOptionalFixed(+usageAllocation, 2) : '-'
            } xKIM`,
            `${
              usageAllocation
                ? (+usageAllocation + +state.amount).toFixed(2)
                : '-'
            } xKIM`
          )}
          {renderInfoRow(
            'Allocation share',
            `${dividendsShare ? `${dividendsShare.toFixed(2)}%` : '-'}`,
            `${getNextDividendsShare().toFixed(2)}%`
          )}
          {/*{renderInfoRow('Daily returns / xKIM', `n/a`)}*/}
          {/*{renderInfoRow('Total daily returns', `n/a`)}*/}
        </>
      )}
      {renderSubmitAction()}
      <TransactionResultModal
        isOpen={!!feedback}
        status={feedback?.status}
        title={feedback?.title}
        message={feedback?.message}
        txHash={feedback?.txHash}
        onClose={() => setFeedback(null)}
      />
    </Box>
  );
};
